import { useCallback, useRef } from 'react'
import NextImage, {
  type ImageLoader,
  ImageProps as NextImageProps,
} from 'next/image'
import { cloudflareLoader } from '@mc/image-cloudflare'

export type ImageProps = Omit<NextImageProps, 'loader'> & {
  /** Called when the image fails to load, including pre-hydration failures that `onError` alone misses. */
  onLoadFail?: () => void
  /** Cloudflare server-side blur radius (1-250). The sharp asset never
   *  reaches the client — use this over a CSS blur for gated artwork.
   *  Contentful (ctfassets.net) sources ignore it: the loader passes those
   *  URLs through with only a width rewrite. */
  blur?: number
}

/**
 * The loader `Image` and `preloadImage` share. A preload only warms the URL
 * the image requests when both build their srcset with the same loader.
 */
export const imageLoader =
  (blur?: number): ImageLoader =>
  (loaderProps) =>
    cloudflareLoader({ ...loaderProps, blur })

export const Image = ({
  src,
  onLoadFail,
  onError,
  blur,
  ...props
}: ImageProps) => {
  const onLoadFailRef = useRef(onLoadFail)
  onLoadFailRef.current = onLoadFail

  // SSR-fetched images can fail before hydration, and error events don't
  // replay — detect already-failed imgs at ref-attach time too.
  const failureDetectingRef = useCallback((img: HTMLImageElement | null) => {
    if (img?.complete && img.naturalWidth === 0) onLoadFailRef.current?.()
  }, [])

  if (!src) return null

  return (
    <NextImage
      {...props}
      loader={imageLoader(blur)}
      src={src}
      ref={failureDetectingRef}
      onError={(e) => {
        onError?.(e)
        onLoadFailRef.current?.()
      }}
    />
  )
}
